import { Component, OnDestroy, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { distinctUntilChanged, filter, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import {
  PesquisaCodigoTributacaoModalComponent,
  PesquisaCodigoTributacaoModalData,
  PesquisaCodigoTributacaoModalResult,
  PesquisaModalItem,
} from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { Masks, MaskService } from '@shared/services/mask.service';
import { CnaeService } from '@shared/services/apis/servico/cnae.service';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { CnaeRequest } from '@shared/services/requests/servico/cnae.request';
import { CreateCnaeResponse } from '@shared/services/responses/servico/create-cnae.response';
import { HeaderComponent } from '@shared/components/header/header.component';

@Component({
  selector: 'app-cnae-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    NgxMaskDirective,
    LoadingBoxComponent,
    FooterActionComponent,
    HeaderComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './cnae-cadastro.component.html',
  styleUrl: './cnae-cadastro.component.css',
})
export class CnaeCadastroComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public readonly tamanhoMaximoDescricao: number = 250;

  public masks: Masks;
  public form: FormGroup;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idCnae: number = 0;

  private descricaoCatalogo: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private cnaeService: CnaeService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private maskService: MaskService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.masks = this.maskService.getMasks();
    this.createForm();
    this.observarCodigo();
    this.route.params.subscribe(params => {
      if (!params['idCnae']) return;
      this.idCnae = +params['idCnae'];
      this.isEdicao = true;
      this.carregarCnae();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      codigo: ['', Validators.required],
      descricao: ['', [Validators.required, Validators.maxLength(this.tamanhoMaximoDescricao)]],
    });
  }

  private observarCodigo(): void {
    this.form
      .get('codigo')
      ?.valueChanges.pipe(
        map((valor: string | null) => (valor ?? '').replace(/\D/g, '')),
        distinctUntilChanged(),
        tap(codigo => {
          if (!codigo) this.limparDescricao();
        }),
        filter(codigo => codigo.length === 7),
        switchMap(codigo => this.buscarCnaeCatalogoPorCodigo(codigo)),
        takeUntil(this.destroy$)
      )
      .subscribe(item => this.preencherDescricaoCatalogo(item));
  }

  private buscarCnaeCatalogoPorCodigo(codigo: string): Observable<PesquisaModalItem | undefined> {
    return this.buscarCnaesCatalogo(codigo).pipe(
      map(resultado => resultado.itens.find(item => item.codigo.replace(/\D/g, '') === codigo))
    );
  }

  private preencherDescricaoCatalogo(item: PesquisaModalItem | undefined): void {
    if (!item) return;
    const descricaoAtual: string = this.form.get('descricao')?.value ?? '';
    if (descricaoAtual && descricaoAtual !== this.descricaoCatalogo) return;
    this.descricaoCatalogo = item.descricao.slice(0, this.tamanhoMaximoDescricao);
    this.form.get('descricao')?.setValue(this.descricaoCatalogo);
  }

  private limparDescricao(): void {
    const descricaoAtual: string = this.form.get('descricao')?.value ?? '';
    if (descricaoAtual === this.descricaoCatalogo) this.form.get('descricao')?.setValue('');
    this.descricaoCatalogo = '';
  }

  private carregarCnae(): void {
    this.isLoading = true;
    this.cnaeService.getById(this.idCnae).subscribe({
      next: res => this.tratarRetornoCarregamento(res),
      error: () => {
        this.isLoading = false;
        this.alertService.show(
          'Aviso',
          'Não foi possível carregar o CNAE agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
        this.voltar();
      },
    });
  }

  private tratarRetornoCarregamento(res: CreateCnaeResponse): void {
    this.isLoading = false;
    if (res.isError) {
      this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
      this.voltar();
      return;
    }
    if (res.cnae) {
      this.form.patchValue({ codigo: res.cnae.codigo, descricao: res.cnae.descricao }, { emitEvent: false });
    }
  }

  public pesquisarCnae(): void {
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar CNAE',
        buscarFn: (pesquisa: string): Observable<{ isError: boolean; itens: PesquisaModalItem[] }> =>
          this.buscarCnaesCatalogo(pesquisa),
        permitirManual: false,
        registrosIniciais: 20,
        valorInicial: this.form.get('codigo')?.value || undefined,
      } satisfies PesquisaCodigoTributacaoModalData,
    });
    dialogRef
      .afterClosed()
      .subscribe((result: PesquisaCodigoTributacaoModalResult | null) =>
        this.preencherCnaeSelecionado(result)
      );
  }

  private buscarCnaesCatalogo(
    pesquisa: string
  ): Observable<{ isError: boolean; itens: PesquisaModalItem[] }> {
    return this.motorFiscalLookupService.getCnaes(pesquisa).pipe(
      map(lista => ({
        isError: false,
        itens: lista.map(item => ({
          codigo: item.codigo ?? '',
          descricao: item.descricao.replace(`${item.codigo} — `, ''),
        })),
      }))
    );
  }

  private preencherCnaeSelecionado(result: PesquisaCodigoTributacaoModalResult | null): void {
    if (!result?.selecionado) return;
    this.descricaoCatalogo = result.selecionado.descricao.slice(0, this.tamanhoMaximoDescricao);
    this.form.patchValue(
      { codigo: result.selecionado.codigo.replace(/\D/g, ''), descricao: this.descricaoCatalogo },
      { emitEvent: false }
    );
    this.form.markAsDirty();
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Aviso', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }
    this.isSaving = true;
    const request = this.montarRequest();
    if (this.isEdicao) {
      this.cnaeService
        .update(this.idCnae, request)
        .subscribe({ next: res => this.tratarRetorno(res), error: () => this.tratarErro() });
    } else {
      this.cnaeService
        .create(request)
        .subscribe({ next: res => this.tratarRetorno(res), error: () => this.tratarErro() });
    }
  }

  private montarRequest(): CnaeRequest {
    return {
      codigo: (this.form.get('codigo')?.value ?? '').replace(/\D/g, ''),
      descricao: this.form.get('descricao')?.value,
    };
  }

  private tratarRetorno(res: CreateCnaeResponse): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
    } else {
      this.alertService.show('Sucesso', 'CNAE salvo com sucesso.', AlertType.success);
      this.voltar();
    }
  }

  private tratarErro(): void {
    this.isSaving = false;
    this.alertService.show(
      'Aviso',
      'Não foi possível salvar o CNAE agora. Tente novamente em alguns instantes.',
      AlertType.warning
    );
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/servicos/cnaes']);
  }
}
